import { useEffect, useState } from 'react';
import Modal from './Modal';
import { submitServiceEnquiry } from '../services/serviceEnquiry';

const EMPTY_FORM = { fullName: '', phone: '', email: '', socialLink: '', reason: '' };

const inputClass =
  "w-full h-11 rounded-lg border border-[#C8C7C7] bg-white px-3 text-sm text-[#0E2A46] placeholder-[#A0A0A0] outline-none focus:border-[#009183] font-['Inter']";

const Field = ({ label, required, children }) => (
  <label className="block text-left">
    <span className="mb-1 block text-sm font-medium text-[#0E2A46] font-['Poppins']">
      {label}
      {required && <span className="text-[#FF5722]"> *</span>}
    </span>
    {children}
  </label>
);

// Popup behind the "Connect" button on the alumni cards. The request is sent
// as an "Alumni Connect" website enquiry, so it shows up as a lead for staff
// to review before introducing the visitor to the alumnus.
export default function AlumniConnectModal({ open, alumniName, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError('');
    setSubmitted(false);
  }, [open]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) return setError('Please enter your name');
    if (form.phone.replace(/\D/g, '').length < 10) return setError('Please enter a valid phone number');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError('Please enter a valid email address');
    if (!form.socialLink.trim()) return setError('Please enter your social media link');
    if (!form.reason.trim()) return setError('Please tell us why you want to connect');

    setError('');
    setSubmitting(true);
    try {
      await submitServiceEnquiry('alumni-connect', { ...form, alumniName });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={alumniName ? `Connect with ${alumniName}` : 'Connect with our Alumni'}
      widthClass="max-w-md"
      heightClass="max-h-[90vh]"
    >
      {submitted ? (
        <div className="py-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#009183]/10 text-[#009183]">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-base text-[#0E2A46] font-['Inter'] leading-relaxed">
            Your request has been submitted. Our team will review it and connect you with the alumni soon.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-6 rounded-full bg-[#C6521C] px-8 py-2 text-sm font-semibold text-white hover:bg-[#A9461A] transition-colors"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Field label="Name" required>
            <input className={inputClass} value={form.fullName} onChange={update('fullName')} placeholder="Your full name" autoComplete="name" />
          </Field>
          <Field label="Phone Number" required>
            <input className={inputClass} type="tel" value={form.phone} onChange={update('phone')} placeholder="Your phone number" autoComplete="tel" />
          </Field>
          <Field label="Email" required>
            <input className={inputClass} type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" autoComplete="email" />
          </Field>
          <Field label="Social Media Link" required>
            <input className={inputClass} type="url" value={form.socialLink} onChange={update('socialLink')} placeholder="LinkedIn / Instagram profile URL" />
          </Field>
          <Field label="Reason to Connect" required>
            <textarea
              className={`${inputClass} h-28 py-2 resize-none`}
              value={form.reason}
              onChange={update('reason')}
              maxLength={1000}
              placeholder="What would you like to talk about?"
            />
          </Field>

          {error && <p className="text-sm text-red-600 text-left">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-[#C6521C] py-2.5 text-sm font-semibold text-white hover:bg-[#A9461A] transition-colors disabled:opacity-60"
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      )}
    </Modal>
  );
}
